import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import type { ParkingSpot, Vehicle } from '../types';

const SPOT_TYPE_COLORS = {
  regular: '#2196f3',
  maintenance: '#ff9800',
  washing: '#9c27b0',
  premium: '#4caf50',
};

// Component to display the current parking layout
const CurrentLayoutDisplay: React.FC<{ 
  layout: any; 
  parkingSpots: ParkingSpot[];
  onSpotClick?: (spotData: any) => void;
  getSpotColor?: (visualSpot: any) => string;
  getSpotText?: (visualSpot: any) => string;
}> = ({ layout, parkingSpots, onSpotClick, getSpotColor: customGetSpotColor, getSpotText: customGetSpotText }) => {
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [lastTouchDistance, setLastTouchDistance] = useState(0);
  const [lastTouchCenter, setLastTouchCenter] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-fit the parking space to show the full layout
  useEffect(() => {
    if (containerRef.current && layout.canvas) {
      const container = containerRef.current;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      
      const layoutWidth = layout.canvas.width || 1400;
      const layoutHeight = layout.canvas.height || 900;
      
      // Calculate zoom to fit the entire layout with some padding
      const padding = 20; // 20px padding on all sides
      const scaleX = (containerWidth - padding * 2) / layoutWidth;
      const scaleY = (containerHeight - padding * 2) / layoutHeight;
      const autoZoom = Math.min(scaleX, scaleY, 1); // Don't zoom in beyond 100%
      
      setZoom(autoZoom);
      setPanOffset({ x: 0, y: 0 }); // Center the layout
    }
  }, [layout.canvas]);

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.1, Math.min(3, zoom * delta));
    setZoom(newZoom);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0) { // Left mouse button
      setIsDragging(true);
      setDragStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPanOffset({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch event handlers for mobile
  const getTouchDistance = (touches: React.TouchList) => {
    if (touches.length < 2) return 0;
    const touch1 = touches[0];
    const touch2 = touches[1];
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getTouchCenter = (touches: React.TouchList) => {
    if (touches.length === 0) return { x: 0, y: 0 };
    if (touches.length === 1) {
      return { x: touches[0].clientX, y: touches[0].clientY };
    }
    // For two touches, return the center point
    const touch1 = touches[0];
    const touch2 = touches[1];
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2
    };
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    const touches = e.touches;
    
    if (touches.length === 1) {
      // Single touch - start panning
      setIsDragging(true);
      setDragStart({ x: touches[0].clientX - panOffset.x, y: touches[0].clientY - panOffset.y });
    } else if (touches.length === 2) {
      // Two touches - prepare for zoom
      const distance = getTouchDistance(touches);
      const center = getTouchCenter(touches);
      setLastTouchDistance(distance);
      setLastTouchCenter(center);
      setIsDragging(false);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    const touches = e.touches;
    
    if (touches.length === 1 && isDragging) {
      // Single touch - pan
      setPanOffset({
        x: touches[0].clientX - dragStart.x,
        y: touches[0].clientY - dragStart.y,
      });
    } else if (touches.length === 2) {
      // Two touches - zoom and pan
      const distance = getTouchDistance(touches);
      const center = getTouchCenter(touches);
      
      if (lastTouchDistance > 0) {
        const scale = distance / lastTouchDistance;
        const newZoom = Math.max(0.1, Math.min(3, zoom * scale));
        setZoom(newZoom);
        
        // Adjust pan offset to zoom around the center point
        const centerX = center.x - (containerRef.current?.offsetLeft || 0);
        const centerY = center.y - (containerRef.current?.offsetTop || 0);
        
        setPanOffset({
          x: centerX - (centerX - panOffset.x) * scale,
          y: centerY - (centerY - panOffset.y) * scale,
        });
      }
      
      setLastTouchDistance(distance);
      setLastTouchCenter(center);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    if (e.touches.length === 0) {
      setIsDragging(false);
      setLastTouchDistance(0);
    }
  };

  const resetView = () => {
    if (containerRef.current && layout.canvas) {
      const container = containerRef.current;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      
      const layoutWidth = layout.canvas.width || 1400;
      const layoutHeight = layout.canvas.height || 900;
      
      // Calculate zoom to fit the entire layout with some padding
      const padding = 20; // 20px padding on all sides
      const scaleX = (containerWidth - padding * 2) / layoutWidth;
      const scaleY = (containerHeight - padding * 2) / layoutHeight;
      const autoZoom = Math.min(scaleX, scaleY, 1); // Don't zoom in beyond 100%
      
      setZoom(autoZoom);
      setPanOffset({ x: 0, y: 0 }); // Center the layout
    } else {
      setZoom(1);
      setPanOffset({ x: 0, y: 0 });
    }
  };

  // Function to get real parking spot data for a visual spot
  const getRealSpotData = (visualSpot: any) => {
    if (!visualSpot.assigned_spot_id) return null;
    return parkingSpots.find(spot => spot.id.toString() === visualSpot.assigned_spot_id.toString());
  };

  // Function to get spot color based on real data
  const getSpotColor = (visualSpot: any) => {
    // Use custom function if provided
    if (customGetSpotColor) {
      return customGetSpotColor(visualSpot);
    }
    
    const realSpot = getRealSpotData(visualSpot);
    if (!realSpot) {
      // No real data - use original color or gray
      return visualSpot.color || '#e0e0e0';
    }
    
    // Use real data to determine color
    if (realSpot.is_occupied) {
      return '#f44336'; // Red for occupied
    }
    
    // Return normal color based on spot type
    return SPOT_TYPE_COLORS[realSpot.spot_type as keyof typeof SPOT_TYPE_COLORS] || visualSpot.color || '#e0e0e0';
  };

  // Function to get spot text (license plate or spot number)
  const getSpotText = (visualSpot: any) => {
    // Use custom function if provided
    if (customGetSpotText) {
      return customGetSpotText(visualSpot);
    }
    
    const realSpot = getRealSpotData(visualSpot);
    if (!realSpot) {
      return visualSpot.text || (visualSpot.assigned_spot_id ? (visualSpot.spot_number || 'N/A') : '');
    }
    
    // Show license plate if occupied, otherwise show spot number
    if (realSpot.is_occupied && realSpot.vehicle) {
      return realSpot.vehicle.registration_number;
    }
    
    return realSpot.spot_number || visualSpot.spot_number || 'N/A';
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        backgroundColor: layout.canvas?.background_color || '#ffffff',
        backgroundImage: layout.canvas?.background_image ? `url(${layout.canvas.background_image})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
        overflow: 'hidden',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        touchAction: 'none', // Prevent default touch behaviors
        WebkitTouchCallout: 'none', // Prevent iOS callout
        WebkitUserSelect: 'none', // Prevent text selection on iOS
      }}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Zoom Controls */}
      <Box sx={{ 
        position: 'absolute', 
        top: 10, 
        right: 10, 
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 1
      }}>
        <Button
          size="small"
          variant="contained"
          onClick={() => setZoom(Math.min(3, zoom * 1.2))}
          sx={{ minWidth: 'auto', width: 32, height: 32, p: 0 }}
        >
          +
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => setZoom(Math.max(0.1, zoom / 1.2))}
          sx={{ minWidth: 'auto', width: 32, height: 32, p: 0 }}
        >
          -
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={resetView}
          sx={{ minWidth: 'auto', width: 32, height: 32, p: 0, fontSize: '10px' }}
        >
          ⌂
        </Button>
      </Box>
      
      {/* Zoom Level Display */}
      <Box sx={{ 
        position: 'absolute', 
        top: 10, 
        left: 10, 
        zIndex: 10,
        backgroundColor: 'rgba(0,0,0,0.7)',
        color: 'white',
        px: 1,
        py: 0.5,
        borderRadius: 1,
        fontSize: '12px'
      }}>
        {Math.round(zoom * 100)}%
      </Box>

      {/* Content Container */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: `translate(-50%, -50%) scale(${zoom}) translate(${panOffset.x / zoom}px, ${panOffset.y / zoom}px)`,
          transformOrigin: 'center center',
          width: layout.canvas?.width || 1400,
          height: layout.canvas?.height || 900,
        }}
      >
      {(layout.spots && layout.spots.length > 0) || (layout.elements && layout.elements.length > 0) ? (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          {/* Render visual elements (rectangles, circles, triangles) */}
          {layout.elements && layout.elements.map((element: any) => (
            <Box
              key={element.id}
              sx={{
                position: 'absolute',
                left: element.x || 0,
                top: element.y || 0,
                width: element.width || 80,
                height: element.height || 40,
                backgroundColor: element.color || '#2196f3',
                border: element.borderWidth && element.borderWidth > 0 
                  ? `${element.borderWidth}px solid ${element.borderColor || '#000'}` 
                  : 'none',
                borderRadius: element.type === 'circle' ? '50%' : 1,
                clipPath: element.type === 'triangle' ? 'polygon(50% 0%, 0% 100%, 100% 100%)' : undefined,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: element.fontSize || '0.7rem',
                fontWeight: 'bold',
                color: '#000000',
                cursor: 'default',
                transform: `rotate(${element.rotation || 0}deg)`,
              }}
            >
              {element.text && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: element.fontSize || 12,
                    color: element.textColor || '#000000',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    wordBreak: 'break-word',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    overflow: 'hidden',
                    textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
                  }}
                >
                  {element.text}
                </Typography>
              )}
            </Box>
          ))}
          
          {/* Render visual parking spots */}
          {layout.spots && layout.spots.map((spot: any) => {
            const realSpot = getRealSpotData(spot);
            const spotColor = getSpotColor(spot);
            const spotText = getSpotText(spot);
            const isOccupied = realSpot?.is_occupied || false;
            
            return (
              <Box
                key={spot.id}
                sx={{
                  position: 'absolute',
                  left: spot.x || 0,
                  top: spot.y || 0,
                  width: spot.width || 80,
                  height: spot.height || 40,
                  backgroundColor: spotColor,
                  border: spot.borderWidth && spot.borderWidth > 0 
                    ? `${spot.borderWidth}px solid ${spot.borderColor || '#000'}` 
                    : '1px solid #333',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: spot.fontSize || '0.7rem',
                  fontWeight: 'bold',
                  color: isOccupied ? 'white' : (realSpot ? 'white' : '#666'),
                  cursor: onSpotClick ? 'pointer' : 'default',
                  transform: `rotate(${spot.rotation || 0}deg)`,
                  textShadow: isOccupied ? '1px 1px 2px rgba(0,0,0,0.7)' : (realSpot ? '1px 1px 2px rgba(0,0,0,0.7)' : 'none'),
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: `rotate(${spot.rotation || 0}deg) scale(1.05)`,
                    zIndex: 10,
                    boxShadow: 2,
                  }
                }}
                title={`${spotText} - ${isOccupied ? 'Occupé' : 'Disponible'}${realSpot?.vehicle ? ` - ${realSpot.vehicle.marque} ${realSpot.vehicle.modele}` : ''}`}
                onClick={() => onSpotClick && onSpotClick(spot)}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: Math.min(spot.fontSize || 12, (spot.width || 80) / 8), // Responsive font size
                    color: isOccupied ? 'white' : (realSpot ? 'white' : '#666'),
                    fontWeight: 'bold',
                    textAlign: 'center',
                    userSelect: 'none',
                    lineHeight: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                >
                  {isOccupied && realSpot ? realSpot.spot_number : spotText}
                </Typography>
                {isOccupied && realSpot?.vehicle && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: Math.min((spot.fontSize || 12) * 0.7, (spot.width || 80) / 12),
                      color: 'rgba(255,255,255,0.8)',
                      fontWeight: 'normal',
                      textAlign: 'center',
                      userSelect: 'none',
                      lineHeight: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                    }}
                  >
                    {realSpot.vehicle.registration_number}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography variant="body2" color="textSecondary">
          Aucune place définie
        </Typography>
      )}
      </Box>
    </Box>
  );
};

export default CurrentLayoutDisplay;
